// Superadmin -> Branding: the platform's own login face, plus any company's branding.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { DEFAULT_PLATFORM, loadPlatformBranding, type PlatformBranding, type TenantBranding } from "@/lib/branding";
import { logPlatformAdminAction } from "@/lib/platformAuditLog";
import { PlatformBrandingEditor, TenantBrandingEditor } from "./BrandingEditors";

const SuperadminBranding: React.FC = () => {
  const [platform, setPlatform] = useState<PlatformBranding>(DEFAULT_PLATFORM);
  const [tenants, setTenants] = useState<TenantBranding[]>([]);
  const [selected, setSelected] = useState<string>("");

  const load = useCallback(async () => {
    const [p, t] = await Promise.all([
      loadPlatformBranding(),
      supabase
        .from("tenants")
        .select("id, tenant_name, display_name, logo_url, favicon_url, brand_primary, brand_secondary, brand_background, login_tagline")
        .order("tenant_name"),
    ]);
    setPlatform(p);
    const rows = ((t.data as TenantBranding[]) || []);
    setTenants(rows);
    setSelected((cur) => cur || rows[0]?.id || "");
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const tenant = tenants.find((x) => x.id === selected) ?? null;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Platform branding</CardTitle>
          <CardDescription>
            What everyone sees on the sign-in screen <em>before</em> they enter a company code. Nothing here belongs to any one company.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <PlatformBrandingEditor
            initial={platform}
            onSaved={(b) => {
              setPlatform(b);
              logPlatformAdminAction("platform_branding_changed", "platform_branding", null, { product_name: b.product_name });
            }}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Company branding</CardTitle>
          <CardDescription>
            A company's own name, logo, tab icon and colours, shown once its code is entered. Its admins can also change these themselves
            under Settings → Branding.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-w-xs">
            <Label>Company</Label>
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.display_name || t.tenant_name}
                </option>
              ))}
            </select>
          </div>
          {tenant && (
            <TenantBrandingEditor
              key={tenant.id}
              tenantId={tenant.id}
              tenantName={tenant.tenant_name}
              initial={tenant}
              onSaved={(b) => {
                setTenants((prev) => prev.map((x) => (x.id === b.id ? { ...x, ...b } : x)));
                logPlatformAdminAction("tenant_branding_changed", "tenant", b.id, { display_name: b.display_name });
              }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperadminBranding;
